<script setup lang="ts">
	import { format, formatDistanceToNow } from 'date-fns'
	import { es } from 'date-fns/locale'
	import { computed, reactive, ref, watch } from 'vue'

	import { useAuth } from '@/composables/useAuth'

	import useCompany from '@/modules/companies/composables/useCompany'
	import type { Company } from '@/modules/companies/interfaces/company'

	import PlaneReportFiltres from '../components/PlaneReportFiltres.vue'
	import PlaneReportsTable from '../components/PlaneReportsTable.vue'

	export interface PlaneFilters {
		dateRange: string[] | null
		creationDateRange: string[] | null | undefined
		statusFilter: string
		datePreset: string
		creationDatePreset: string
		searchTravelerId: number | null
		searchTravelerName: string | null
		createdAt: string[] | null
		orderBy: string
		orderDirection: 'asc' | 'desc'
	}

	const showFilters = ref(false)

	const { user } = useAuth()

	const { company } = useCompany(
		{
			companyId: user.value?.companyId,
		},
		{
			immediate: !user.value?.internal,
		}
	)

	const selectedCompany = ref<Company | null>(company.value ?? null)

	watch(
		() => company.value,
		(newCompany) => {
			if (newCompany) {
				selectedCompany.value = newCompany
			}
		},
		{ immediate: true }
	)

	const filters = reactive<PlaneFilters>({
		dateRange: null as string[] | null,
		creationDateRange: null as string[] | null,
		statusFilter: 'all',
		datePreset: '1month',
		creationDatePreset: '',
		searchTravelerId: null,
		searchTravelerName: null,
		createdAt: null as string[] | null,
		orderBy: 'created_at',
		orderDirection: 'desc',
	})

	const handleFiltersUpdate = (newFilters: PlaneFilters) => {
		Object.assign(filters, newFilters)
	}

	const activeFiltersCount = computed(() => {
		let count = 0
		if (filters.searchTravelerId) count++
		if (filters.statusFilter !== 'all') count++
		if (filters.dateRange && filters.dateRange.length > 0) count++
		if (
			(filters.creationDateRange && filters.creationDateRange.length > 0) ||
			(filters.creationDatePreset && filters.creationDatePreset !== '')
		)
			count++
		if (filters.orderBy !== 'created_at' || filters.orderDirection !== 'desc') count++
		return count
	})

	const getStatusLabel = (status: string): string => {
		const statusLabels: Record<string, string> = {
			pending: 'Pendiente',
			confirmed: 'Confirmada',
			cancelled: 'Cancelada',
			completed: 'Completada',
			in_progress: 'En progreso',
		}
		return statusLabels[status] || status
	}

	const formatDateRange = (dateRange: string[] | null | undefined): string => {
		if (!dateRange || dateRange.length === 0) return ''

		const startDate = new Date(dateRange[0])
		const endDate = new Date(dateRange[1])
		const now = new Date()

		const todayStart = new Date(
			now.getFullYear(),
			now.getMonth(),
			now.getDate()
		)
		const startDateNormalized = new Date(
			startDate.getFullYear(),
			startDate.getMonth(),
			startDate.getDate()
		)
		const endDateNormalized = new Date(
			endDate.getFullYear(),
			endDate.getMonth(),
			endDate.getDate()
		)

		const isFutureRange =
			startDateNormalized.getTime() === todayStart.getTime() &&
			endDateNormalized.getTime() > todayStart.getTime()

		if (isFutureRange) {
			const distance = formatDistanceToNow(endDate, {
				locale: es,
				addSuffix: false,
			})
			return `Próximos ${distance}`
		}

		const isPastRange =
			endDateNormalized.getTime() === todayStart.getTime() &&
			startDateNormalized.getTime() < todayStart.getTime()

		if (isPastRange) {
			const distance = formatDistanceToNow(startDate, {
				locale: es,
				addSuffix: false,
			})
			return `Últimos ${distance}`
		}

		const start = format(startDate, 'dd/MM/yyyy', { locale: es })
		const end = format(endDate, 'dd/MM/yyyy', { locale: es })
		return `${start} - ${end}`
	}

	const getOrderLabel = (orderBy: string, direction: string): string => {
		const orderLabels: Record<string, string> = {
			created_at: 'Fecha de creación',
			start_date: 'Fecha de salida',
			end_date: 'Fecha de regreso',
			total: 'Total',
			traveler_name: 'Nombre del viajero',
			suppliable_name: 'Aerolínea',
		}
		const directionLabel = direction === 'asc' ? 'Ascendente' : 'Descendente'
		return `${orderLabels[orderBy] || orderBy} (${directionLabel})`
	}

	const presetLabels: Record<string, string> = {
		'1monthNext': 'Próximo mes',
		'3monthNext': 'Próximos 3 meses',
		'6monthNext': 'Próximos 6 meses',
		year: 'Este año',
		'1month': 'Último mes',
		'3months': 'Últimos 3 meses',
		'6months': 'Últimos 6 meses',
		lastYear: 'Último año',
	}

	const getDateRangeLabel = (): string => {
		if (filters.datePreset === 'custom' && filters.dateRange) {
			return formatDateRange(filters.dateRange)
		}
		return presetLabels[filters.datePreset] || 'Período de tiempo'
	}

	const getCreationDateLabel = (): string => {
		if (filters.creationDatePreset === 'custom' && filters.creationDateRange) {
			return formatDateRange(filters.creationDateRange)
		}
		return presetLabels[filters.creationDatePreset] || 'Fecha de creación'
	}
</script>

<template>
	<div class="min-h-screen bg-gray-50 py-6">
		<div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
			<!-- Header -->
			<div class="mb-6">
				<h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
					Reportes de Vuelos
				</h1>
				<p class="mt-2 text-xs sm:text-sm text-gray-600">
					Análisis detallado de las reservas y métricas de vuelos
				</p>
			</div>

			<!-- Controles superiores -->
			<div class="mb-6 space-y-4">
				<div
					class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3"
				>
					<!-- Link a hotel reports -->
					<RouterLink
						:to="{ name: 'reports.hotels' }"
						class="inline-flex items-center text-sm text-gray-600 hover:text-blue-ef-500 transition-colors"
					>
						<span class="material-symbols-outlined mr-1 text-lg">
							hotel
						</span>
						<span>Ver reportes de hoteleria</span>
					</RouterLink>

					<!-- Boton filtros -->
					<button
						type="button"
						class="inline-flex items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 relative w-full sm:w-auto"
						@click="showFilters = true"
					>
						<span class="material-symbols-outlined mr-2 text-lg">
							filter_list
						</span>
						<span>Filtros</span>
						<span
							v-if="activeFiltersCount > 0"
							class="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full"
						>
							{{ activeFiltersCount }}
						</span>
					</button>
				</div>

				<!-- Filtros activos como badges -->
				<div
					v-if="activeFiltersCount > 0"
					class="flex flex-wrap gap-2"
				>
					<!-- Viajero -->
					<span
						v-if="filters.searchTravelerId"
						class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
					>
						<span class="material-symbols-outlined text-sm">person</span>
						<span class="truncate max-w-[120px] sm:max-w-none">{{
							filters.searchTravelerName || 'Viajero'
						}}</span>
						<button
							@click="
								() => {
									filters.searchTravelerId = null
									filters.searchTravelerName = null
								}
							"
							class="ml-0.5 hover:bg-blue-200 rounded-full p-0.5"
						>
							<span class="material-symbols-outlined text-sm cursor-pointer"
								>close</span
							>
						</button>
					</span>
					<!-- Estado -->
					<span
						v-if="filters.statusFilter !== 'all'"
						class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
					>
						<span class="material-symbols-outlined text-sm"
							>check_circle</span
						>
						<span class="truncate"
							>Estatus: {{ getStatusLabel(filters.statusFilter) }}</span
						>
						<button
							@click="filters.statusFilter = 'all'"
							class="ml-0.5 hover:bg-green-200 rounded-full p-0.5"
						>
							<span class="material-symbols-outlined text-sm cursor-pointer"
								>close</span
							>
						</button>
					</span>
					<!-- Rango de fechas -->
					<span
						v-if="filters.dateRange && filters.dateRange.length > 0"
						class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
					>
						<span class="material-symbols-outlined text-sm"
							>calendar_today</span
						>
						<span class="truncate max-w-[150px] sm:max-w-none"
							>Creado: {{ getDateRangeLabel() }}</span
						>
					</span>
					<!-- Rango de fechas de creacion -->
					<span
						v-if="
							(filters.creationDateRange &&
								filters.creationDateRange.length > 0) ||
							(filters.creationDatePreset &&
								filters.creationDatePreset !== '')
						"
						class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800"
					>
						<span class="material-symbols-outlined text-sm">event</span>
						<span class="truncate max-w-[150px] sm:max-w-none"
							>Creacion: {{ getCreationDateLabel() }}</span
						>
					</span>
					<!-- Ordenamiento -->
					<span
						class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
					>
						<span class="material-symbols-outlined text-sm">sort</span>
						<span class="hidden sm:inline">Ordenar:</span>
						<span class="truncate max-w-[120px] sm:max-w-none">
							{{ getOrderLabel(filters.orderBy, filters.orderDirection) }}
						</span>
					</span>
				</div>
			</div>

			<!-- Tabla -->
			<div class="rounded-lg bg-white shadow">
				<PlaneReportsTable
					:company-id="selectedCompany?.id"
					:date-range="filters.dateRange"
					:creation-date-range="filters.creationDateRange"
					:date-preset="filters.datePreset"
					:creation-date-preset="filters.creationDatePreset"
					:status-filter="filters.statusFilter"
					:search-traveler-id="filters.searchTravelerId"
					:created-at="filters.createdAt"
					:order-by="filters.orderBy"
					:order-direction="filters.orderDirection"
				/>
			</div>
		</div>
	</div>

	<PlaneReportFiltres
		:filtres="filters"
		:selected-company="selectedCompany"
		:show-filters="showFilters"
		@update:show-filters="showFilters = $event"
		@update:filtres="handleFiltersUpdate"
		@update:selected-company="selectedCompany = $event"
	/>
</template>
